import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import env from 'js/constants/env';
import { useCurrentOrg } from './adapters/useCurrentOrg';

type UIVersion = 'v1' | 'v2';
type UIVersionLabel = 'stable' | 'next';
type BuildMode = 'single' | 'dual';

interface VersionResponse {
  version: UIVersion;
  versionLabel: UIVersionLabel;
  current_cookie: string;
  serving: UIVersion;
  servingLabel: UIVersionLabel;
  reload_required: boolean;
  org_id: string | null;
  build_mode: BuildMode;
  timestamp: number;
  error?: string;
}

// CRITICAL: Use env() to read runtime config from window.__ENV (set by __ENV.js)
// This allows BUILD_MODE to be injected at runtime rather than baked in at build time.
// This is essential for dual-branch deploys where:
// - Each branch builds separately with BUILD_MODE=single (Dockerfile builds one version)
// - The final container runs with BUILD_MODE=dual (docker_entrypoint sets REACT_APP_BUILD_MODE=dual in __ENV.js)
// - The frontend needs to know it's in dual mode to enable version checking
const VALID_VERSIONS: readonly UIVersion[] = ['v1', 'v2'];
const rawVersion = env('UI_VERSION');
// Validate — if REACT_APP_UI_VERSION is missing or unrecognised (e.g. typo in build arg),
// default to v1 (stable). Prevents infinite reload loops on bad build configuration.
const CURRENT_VERSION: UIVersion = VALID_VERSIONS.includes(rawVersion as UIVersion)
  ? (rawVersion as UIVersion)
  : 'v1';
// Only exact string 'dual' enables the hook — any typo or unrecognised value (e.g. 'Dual',
// 'yes', 'foobar') is treated as single, preventing unintended version-switching behaviour.
const BUILD_MODE: BuildMode = env('BUILD_MODE') === 'dual' ? 'dual' : 'single';

const getVersionLabel = (v: UIVersion): UIVersionLabel => (v === 'v2' ? 'next' : 'stable');

function isEmbeddedMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return new URLSearchParams(window.location.search).get('embedded') === 'true';
}

/**
 * Hook for automatic app version switching.
 *
 * In DUAL mode: Checks the version API on org change and auto-reloads if the
 * org's GrowthBook-targeted version differs from the currently running build.
 *
 * In SINGLE mode: Does nothing — exits before any API call.
 *
 * Silently reloads when version changes — no user prompt.
 */
export function useAppVersion(): void {
  const org = useCurrentOrg();
  const isAuthenticated = useSelector((state: any) => !!state.session?.accessToken);

  const lastCheckedOrgRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const checkVersion = useCallback(
    async (orgId: string) => {
      // Early exit: In single build mode, no version switching is possible
      if (BUILD_MODE === 'single') return;

      // Cross-site iframes cannot persist ui_version cookies (SameSite=Lax),
      // so version checks would reload forever. Parent app owns the embed URL.
      if (isEmbeddedMode()) return;

      if (!isAuthenticated) return;

      // Cancel any pending debounced check
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce to avoid excessive API calls when org changes rapidly
      debounceTimerRef.current = setTimeout(async () => {
        // Skip if we already checked this exact org in the current debounce window
        if (lastCheckedOrgRef.current === orgId) return;

        lastCheckedOrgRef.current = orgId;

        try {
          const response = await fetch(`/api/version?org_id=${encodeURIComponent(orgId)}`, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) return;

          const data: VersionResponse = await response.json();

          // CURRENT_VERSION is what this frontend build was compiled with.
          // data.version is what GrowthBook says this org should see.
          // Guard: only reload for known-valid versions — bad data (e.g. 'v3') is ignored,
          // keeping the user on stable rather than triggering an infinite reload loop.
          if (VALID_VERSIONS.includes(data.version) && CURRENT_VERSION !== data.version) {
            // Small delay to ensure the cookie set by the server is written first
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }
        } catch {
          // Silent fail — don't block the app
        }
      }, 300);
    },
    [isAuthenticated],
  );

  // Tracks the last org we triggered a check for, separate from lastCheckedOrgRef
  // (which deduplicates within a debounce window). This prevents re-triggering
  // on re-renders when the org hasn't actually changed.
  const lastEffectOrgRef = useRef<string | null>(null);

  useEffect(() => {
    if (org?.id && isAuthenticated) {
      const currentOrgId = String(org.id);

      // Only trigger check if org actually changed
      if (lastEffectOrgRef.current !== currentOrgId) {
        // Clear any pending debounce from the previous org
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }

        // Reset so the new org is checked fresh
        lastCheckedOrgRef.current = null;
        lastEffectOrgRef.current = currentOrgId;

        checkVersion(currentOrgId);
      }
    }
  }, [org?.id, isAuthenticated, checkVersion]);
}

/**
 * Clear version cookie — call on logout.
 */
export async function clearVersionCookie(): Promise<void> {
  try {
    await fetch('/api/version/clear', {
      method: 'GET',
      credentials: 'include',
    });
  } catch {
    // Silent fail
  }
}

export function getCurrentUIVersion(): UIVersion {
  return CURRENT_VERSION;
}

export function getCurrentUIVersionLabel(): UIVersionLabel {
  return getVersionLabel(CURRENT_VERSION);
}
